import { navigate } from 'gatsby';
import { trackDocumentClick } from '@src/utils/trackDocumentClick';

/**
 * Get the document identifier used for cart/owned products.
 */
export function getDocumentId(doc) {
  return doc.originalId || doc.name;
}

/**
 * Compute display state for a document (free, purchased, in cart, buy, etc.).
 */
export function getDocumentState(doc, { ownedProducts, miniCart, isSubscribed }) {
  const docId = getDocumentId(doc);
  const isFree = !doc.hasPassword || doc.freeDocument || doc.freeSample;
  const isPurchased = ownedProducts.has(docId);
  const isInCart = miniCart.has(docId);

  if (isFree) return { label: 'FREE SAMPLE', canOpen: true };
  if (isPurchased) return { label: 'Purchased', canOpen: true };
  if (isSubscribed && doc.hasPassword && !doc.freeDocument) return { label: 'View', canOpen: true };
  if (isInCart) return { label: 'Paid', canOpen: false, inCart: true };
  return { label: 'Paid', canOpen: false, inCart: false };
}

/**
 * Handle document click: open modal, navigate to embedded viewer, cart, or add to cart.
 */
export function handleDocumentAction(doc, options) {
  const { ownedProducts, miniCart, isSubscribed, openDocumentInModal, onAddToCart } = options;

  const docId = getDocumentId(doc);
  const isFree = !doc.hasPassword || doc.freeDocument || doc.freeSample;
  const isPurchased = ownedProducts.has(docId);
  const isInCart = miniCart.has(docId);

  // Free documents (FREE SAMPLE) - open in modal using topics download URL (no login required)
  if (isFree) {
    trackDocumentClick(doc, {
      source: 'interactive_learning',
      outcome: 'opened',
      isSubscribed,
    });
    if (doc.originalId && doc.name) {
      openDocumentInModal(doc);
    }
    return;
  }

  // Purchased hasPassword documents: ALWAYS navigate to /embeded (fileUrl requires auth)
  if (isPurchased) {
    trackDocumentClick(doc, {
      source: 'interactive_learning',
      outcome: doc.originalId ? 'opened' : 'profile',
      isSubscribed,
      destinationUrl: doc.originalId ? `/embeded?id=${doc.originalId}` : '/profile',
    });
    if (doc.originalId) {
      navigate(`/embeded?id=${doc.originalId}`);
    } else {
      navigate('/profile');
    }
    return;
  }

  // Subscribed users accessing hasPassword documents: navigate to /embeded
  if (isSubscribed && doc.hasPassword) {
    trackDocumentClick(doc, {
      source: 'interactive_learning',
      outcome: doc.originalId ? 'opened' : 'profile',
      isSubscribed,
      destinationUrl: doc.originalId ? `/embeded?id=${doc.originalId}` : '/profile',
    });
    if (doc.originalId) {
      navigate(`/embeded?id=${doc.originalId}`);
    } else {
      navigate('/profile');
    }
    return;
  }

  // In cart: navigate to cart
  if (isInCart) {
    trackDocumentClick(doc, {
      source: 'interactive_learning',
      outcome: 'cart',
      isSubscribed,
      destinationUrl: '/cart',
    });
    navigate('/cart');
    return;
  }

  // Not purchased/subscribed: add to cart
  trackDocumentClick(doc, {
    source: 'interactive_learning',
    outcome: 'cart',
    isSubscribed,
  });
  onAddToCart(docId);
}

/**
 * Get URL for free sample document (topics download API - no login required).
 * Same pattern as Topics.js.
 */
export function getFreeDocumentModalUrl(doc) {
  if (!doc?.name || !doc?.originalId) return null;
  const base = process.env.GATSBY_BACKEND_URL || '';
  const path = `/api/download/topics/${encodeURIComponent(doc.name)}?id=${doc.originalId}`;
  return `${base}${path}`;
}

/**
 * Get URL to open document in modal.
 * @param {object} doc - The document object
 * @param {boolean} useFullDocument - true: use fileUrl (full doc), false: use previewUrl (preview only)
 */
export function getDocumentModalUrl(doc, useFullDocument) {
  if (useFullDocument) {
    // For free, purchased, or subscribed users: show full document
    return doc.fileUrl || doc.previewUrl;
  }
  // For unpurchased paid documents: show preview only
  return doc.previewUrl;
}
